import React, { useState, useEffect, useRef } from 'react';
import { ChevronDown, Info, AlertCircle, X, CheckCircle } from 'lucide-react';
import PlantPermitSignature from './PlantPermitSignature';
import { getLocation } from '../../services/locationService';
import { getDepartments } from '../../services/departmentService';
import { getPlantRequiringIsolation } from '../../services/plantSafetyWorkPermitService';
import { getPSWPResponsiblePersons } from '../../services/plantSafetyWorkPermitService';
import {
  createPlantSafetyWorkPermit,
  createPSWPSignature,
  getPlantSafetyWorkPermitById,
  getPSWPSignatureById,
  getPSWPSignatureByWorkPermitId,
  updatePlantSafetyWorkPermit,
  updatePSWPSignature,
  getPlantCodes,
} from '../../services/plantSafetyWorkPermitService';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const steps = ['Application', 'Signature', 'Preview & Add'];

const HAZARDS_MAX_LENGTH = 100;
const WORK_DESCRIPTION_MAX_LENGTH = 100;

interface Location {
  id: number;
  title: string;
}

interface Department {
  id: number;
  name: string;
}

interface ResponsiblePerson {
  id: number;
  name: string;
}

interface PlantCode {
  id: number;
  title: string;
}

interface CreatePlantPermitModalProps {
  isOpen: boolean;
  onClose: () => void;
  editId?: number | null;
  onSuccess?: () => void;
}

// Rich Text Editor Toolbar Component
const richTextModules = {
  toolbar: [
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
  ],
};

// Strips HTML tags so the character limit counts actual typed text, not markup
const stripHtml = (html: string) => (html || '').replace(/<[^>]*>/g, '').trim();

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  error?: string;
  maxLength?: number;
}
const RichTextEditor: React.FC<RichTextEditorProps> = ({ value, onChange, placeholder, error, maxLength }) => {
  const charCount = stripHtml(value).length;
  const isOverLimit = !!maxLength && charCount > maxLength;

  const handleChange = (val: string) => {
    onChange(val);
  };

  return (
    <div>
      <div
        className={`border ${
          error || isOverLimit ? 'border-red-500' : 'border-gray-300'
        } rounded-md bg-gray-100 [&_.ql-toolbar]:bg-gray-50 [&_.ql-container]:bg-gray-100`}
      >
        <ReactQuill value={value} onChange={handleChange} placeholder={placeholder} modules={richTextModules} theme="snow" />
      </div>

      {maxLength && (
        <div className="flex justify-end mt-1">
          <span className={`text-xs ${isOverLimit ? 'text-red-500 font-medium' : 'text-gray-400'}`}>
            {charCount} / {maxLength}
          </span>
        </div>
      )}

      {isOverLimit && (
        <p className="text-red-500 text-sm mt-1">
          Character limit exceeded. Maximum allowed: {maxLength} characters. Please remove {charCount - maxLength} character
          {charCount - maxLength === 1 ? '' : 's'}.
        </p>
      )}

      {!isOverLimit && error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  );
};

const CreatePlantPermitModal: React.FC<CreatePlantPermitModalProps> = ({ isOpen = true, onClose = () => {}, editId = null, onSuccess }) => {
  const [step, setStep] = useState(0);

  const [locations, setLocations] = useState<Location[]>([]);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [plantCodes, setPlantCodes] = useState<PlantCode[]>([]);
  const [responsiblePersons, setResponsiblePersons] = useState<ResponsiblePerson[]>([]);

  const [isLoadingLocations, setIsLoadingLocations] = useState(true);
  const [isLoadingDepartments, setIsLoadingDepartments] = useState(true);
  const [isLoadingPlantCodes, setIsLoadingPlantCodes] = useState(true);
  const [isLoadingResponsiblePersons, setIsLoadingResponsiblePersons] = useState(true);
  const [isLoadingData, setIsLoadingData] = useState(false);

  const signatureRef = useRef<any>(null);

  const [plantIsolations, setPlantIsolations] = useState<any[]>([]);
  const [isLoadingPlantIsolations, setIsLoadingPlantIsolations] = useState(true);

  const [apiError, setApiError] = useState('');
  const [showError, setShowError] = useState(false);

  // SUCCESS STATE
  const [showSuccess, setShowSuccess] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  const [plantSafetyWorkPermitId, setPlantSafetyWorkPermitId] = useState<number | null>(null);

  const [signatureId, setSignatureId] = useState<number | null>(null);

  const fieldRefs = useRef<any>({});

  const [sigID, setSigID] = useState<number | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formValues, setFormValues] = useState({
    departmentId: '',
    priority: '',
    ewt: '',
    locationId: '',
    unit: '',
    plantCode: '',
    plantRequiringIsolation: '',
    hazards: '',
    workDescription: '',
    ppe: '',
    ptwNo: '',
    environmentCertNo: '',
    gasTestCertNo: '',
    hotWorkApprovalPart: '',
    riskAssessmentNo: '',
    safeEntryCertificate: '',
    workRegisterNumber: '',
  });

  const [signatureValues, setSignatureValues] = useState({
    name: '',
    responsiblePerson: '',
  });

  const [errors, setErrors] = useState({
    departmentId: '',
    priority: '',
    ewt: '',
    locationId: '',
    unit: '',
    plantCode: '',
    plantRequiringIsolation: '',
    hazards: '',
    workDescription: '',
    ppe: '',
    ptwNo: '',
    environmentCertNo: '',
    gasTestCertNo: '',
    hotWorkApprovalPart: '',
    riskAssessmentNo: '',
    safeEntryCertificate: '',
    workRegisterNumber: '',
  });

  const hazardsOverLimit = stripHtml(formValues.hazards).length > HAZARDS_MAX_LENGTH;
  const workDescriptionOverLimit = stripHtml(formValues.workDescription).length > WORK_DESCRIPTION_MAX_LENGTH;

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setIsLoadingLocations(true);

        const data = await getLocation();

        const locationArray = Array.isArray(data) ? data : data?.data || [];

        setLocations(locationArray);
      } catch (error) {
        console.error('❌ Error fetching locations:', error);

        setApiError('Failed to load locations. Please refresh the page.');

        setShowError(true);
      } finally {
        setIsLoadingLocations(false);
      }
    };

    const fetchDepartments = async () => {
      try {
        setIsLoadingDepartments(true);

        const data = await getDepartments();

        const departmentArray = Array.isArray(data) ? data : data?.data || [];

        setDepartments(departmentArray);
      } catch (error) {
        console.error('❌ Error fetching departments:', error);

        setApiError('Failed to load departments. Please refresh the page.');

        setShowError(true);
      } finally {
        setIsLoadingDepartments(false);
      }
    };

    const fetchPlantCodes = async () => {
      try {
        setIsLoadingPlantCodes(true);

        const data = await getPlantCodes();

        const plantCodeArray = Array.isArray(data) ? data : data?.data || [];

        setPlantCodes(plantCodeArray);
      } catch (error) {
        console.error('❌ Error fetching plant codes:', error);

        setApiError('Failed to load plant codes. Please refresh the page.');

        setShowError(true);
      } finally {
        setIsLoadingPlantCodes(false);
      }
    };

    const fetchPlantIsolations = async () => {
      try {
        setIsLoadingPlantIsolations(true);

        const data = await getPlantRequiringIsolation();

        setPlantIsolations(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoadingPlantIsolations(false);
      }
    };

    const fetchResponsiblePersons = async () => {
      try {
        setIsLoadingResponsiblePersons(true);

        const data = await getPSWPResponsiblePersons();

        const list = Array.isArray(data) ? data : data?.data || [];

        if (list.length > 0) {
          setSigID(list[0].id);
        }

        setResponsiblePersons(list);
      } catch (error) {
        console.error(error);
      } finally {
        setIsLoadingResponsiblePersons(false);
      }
    };

    if (isOpen) {
      fetchLocations();
      fetchDepartments();
      fetchPlantCodes();
      fetchPlantIsolations();
      fetchResponsiblePersons();
    }
  }, [isOpen]);

  // Fetch edit data
  useEffect(() => {
    const fetchEditData = async () => {
      if (!editId || !isOpen) return;

      if (isLoadingDepartments || isLoadingLocations || isLoadingPlantCodes || isLoadingPlantIsolations || isLoadingResponsiblePersons) {
        return;
      }

      setIsLoadingData(true);

      try {
        const permitData = await getPlantSafetyWorkPermitById(editId);

        const department = departments.find((d) => d.name === permitData.department);

        const location = locations.find((l) => l.title === permitData.location);

        const plantCode = plantCodes.find((pc) => pc.title === permitData.plant_code);

        const plantIsolation = plantIsolations.find((pi) => pi.title === permitData.plant_requiring_isolation);

        const newFormValues = {
          departmentId: permitData?.department && department ? String(department.id) : '',

          priority: permitData?.priority || '',

          ewt: permitData?.ewt || '',

          locationId: permitData?.location && location ? String(location.id) : '',

          unit: permitData?.unit || '',

          plantCode: plantCode ? String(plantCode.id) : '',

          plantRequiringIsolation: plantIsolation ? String(plantIsolation.id) : '',

          hazards: permitData?.description || '',

          workDescription: permitData?.work || '',

          ppe: permitData?.ppe || '',

          ptwNo: permitData?.ptw_no || '',

          environmentCertNo: permitData?.environment_cert_no || '',

          gasTestCertNo: permitData?.gas_test_cert_no || '',

          hotWorkApprovalPart: permitData?.hot_work_approval || '',

          riskAssessmentNo: permitData?.risk_assessment_no || '',

          safeEntryCertificate: permitData?.safe_entry_certificate || '',

          workRegisterNumber: permitData?.work_register_number || '',
        };

        setFormValues(newFormValues);

        setErrors((prev) => ({
          ...prev,
          hazards:
            stripHtml(newFormValues.hazards).length > HAZARDS_MAX_LENGTH
              ? `Character limit exceeded. Maximum allowed: ${HAZARDS_MAX_LENGTH} characters`
              : '',

          workDescription:
            stripHtml(newFormValues.workDescription).length > WORK_DESCRIPTION_MAX_LENGTH
              ? `Character limit exceeded. Maximum allowed: ${WORK_DESCRIPTION_MAX_LENGTH} characters`
              : '',
        }));

        try {
          const signatureResponse = await getPSWPSignatureByWorkPermitId(editId);

          const signatureData = signatureResponse?.data || signatureResponse;

          if (signatureData) {
            const userId = signatureData.user?.id || signatureData.user_id;

            const newSignatureValues = {
              name: signatureData?.name || '',
              responsiblePerson: userId ? String(userId) : '',
            };

            setSignatureValues(newSignatureValues);

            setSignatureId(signatureData?.id || null);
          }
        } catch (signatureError) {
          console.warn('No signature data found for this permit:', signatureError);

          setSignatureValues({
            name: '',
            responsiblePerson: '',
          });

          setSignatureId(null);
        }

        setPlantSafetyWorkPermitId(editId);
      } catch (error: any) {
        console.error('Error loading edit data:', error);

        const errorMessage =
          error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Failed to load data for editing';

        setApiError(errorMessage);
        setShowError(true);
      } finally {
        setIsLoadingData(false);
      }
    };

    fetchEditData();
  }, [
    editId,
    isOpen,
    isLoadingDepartments,
    isLoadingLocations,
    isLoadingPlantCodes,
    isLoadingPlantIsolations,
    isLoadingResponsiblePersons,
    departments,
    locations,
    plantCodes,
    plantIsolations,
    responsiblePersons,
  ]);

  if (!isOpen) return null;

  const handleClose = () => {
    setStep(0);

    setPlantSafetyWorkPermitId(null);

    setSignatureId(null);

    setFormValues({
      departmentId: '',
      priority: '',
      ewt: '',
      locationId: '',
      unit: '',
      plantCode: '',
      plantRequiringIsolation: '',
      hazards: '',
      workDescription: '',
      ppe: '',
      ptwNo: '',
      environmentCertNo: '',
      gasTestCertNo: '',
      hotWorkApprovalPart: '',
      riskAssessmentNo: '',
      safeEntryCertificate: '',
      workRegisterNumber: '',
    });

    setSignatureValues({
      name: '',
      responsiblePerson: '',
    });

    setErrors({
      departmentId: '',
      priority: '',
      ewt: '',
      locationId: '',
      unit: '',
      plantCode: '',
      plantRequiringIsolation: '',
      hazards: '',
      workDescription: '',
      ppe: '',
      ptwNo: '',
      environmentCertNo: '',
      gasTestCertNo: '',
      hotWorkApprovalPart: '',
      riskAssessmentNo: '',
      safeEntryCertificate: '',
      workRegisterNumber: '',
    });

    setApiError('');
    setShowError(false);

    onClose();
  };

  const focusFirstError = (errorsObj: any) => {
    const firstErrorField = Object.keys(errorsObj).find((key) => errorsObj[key]);

    if (firstErrorField) {
      const field = fieldRefs.current[firstErrorField];

      if (field) {
        field.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });

        setTimeout(() => {
          field.focus();
        }, 300);
      }
    }
  };

  const validateStep0 = () => {
    const newErrors = {
      departmentId: '',
      priority: '',
      ewt: '',
      locationId: '',
      unit: '',
      plantCode: '',
      plantRequiringIsolation: '',
      hazards: '',
      workDescription: '',
      ppe: '',
      ptwNo: '',
      environmentCertNo: '',
      gasTestCertNo: '',
      hotWorkApprovalPart: '',
      riskAssessmentNo: '',
      safeEntryCertificate: '',
      workRegisterNumber: '',
    };

    let isValid = true;

    if (!formValues.departmentId.trim()) {
      newErrors.departmentId = 'Department is required';
      isValid = false;
    }

    if (!formValues.priority.trim()) {
      newErrors.priority = 'Priority is required';
      isValid = false;
    }

    if (!formValues.locationId.trim()) {
      newErrors.locationId = 'Location is required';
      isValid = false;
    }

    if (!formValues.plantCode.trim()) {
      newErrors.plantCode = 'Plant Code is required';
      isValid = false;
    }

    if (!formValues.plantRequiringIsolation.trim()) {
      newErrors.plantRequiringIsolation = 'Plant Requiring Isolation is required';

      isValid = false;
    }

    if (hazardsOverLimit) {
      newErrors.hazards = `Character limit exceeded. Maximum allowed: ${HAZARDS_MAX_LENGTH} characters`;

      isValid = false;
    }

    if (workDescriptionOverLimit) {
      newErrors.workDescription = `Character limit exceeded. Maximum allowed: ${WORK_DESCRIPTION_MAX_LENGTH} characters`;

      isValid = false;
    }

    setErrors(newErrors);

    if (!isValid) {
      focusFirstError(newErrors);
    }

    return isValid;
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      if (step === 0) {
        if (!validateStep0()) {
          return;
        }
        setStep(1);
        return;
      }

      if (step === 1) {
        if (signatureRef.current) {
          const isValid = await signatureRef.current.validateForm();
          if (!isValid) return;

          const sigValues = signatureRef.current.getValues();

          setSignatureValues({
            name: sigValues.name,
            responsiblePerson: String(sigValues.user_id),
          });
        }
        setStep(2);
        return;
      }

      if (step === steps.length - 1) {
        const payload = {
          location_id: Number(formValues.locationId),
          department_id: Number(formValues.departmentId),
          priority: formValues.priority,
          ewt: formValues.ewt,
          unit: formValues.unit || '',
          plant_code_id: Number(formValues.plantCode),
          plant_requiring_isolation_id: Number(formValues.plantRequiringIsolation),
          description: formValues.hazards || '',
          work: formValues.workDescription || '',
          ppe: formValues.ppe || '',
          environment_cert_no: formValues.environmentCertNo || '',
          gas_test_cert_no: formValues.gasTestCertNo || '',
          hot_work_approval: formValues.hotWorkApprovalPart || '',
          risk_assessment_no: formValues.riskAssessmentNo || '',
          safe_entry_certificate: formValues.safeEntryCertificate || '',
          work_register_number: formValues.workRegisterNumber || '',
        };

        let permitId: number | null = editId ?? null;

        if (editId) {
          await updatePlantSafetyWorkPermit(editId, payload);
        } else {
          const response = await createPlantSafetyWorkPermit(payload);
          permitId = response?.id ?? null;
          setPlantSafetyWorkPermitId(permitId);
        }

        const signaturePayload = {
          name: signatureValues.name,
          user_id: Number(signatureValues.responsiblePerson),
          plant_safety_work_permit_id: permitId,
        };

        if (editId && signatureId) {
          await updatePSWPSignature(signatureId, signaturePayload);
        } else {
          const createdSignature = await createPSWPSignature(signaturePayload);
          if (createdSignature?.id) {
            setSignatureId(createdSignature.id);
          }
        }

        const msg = editId ? 'Work permit updated successfully.' : 'Work permit added successfully.';
        setSuccessMessage(msg);
        setShowSuccess(true);

        setTimeout(() => {
          setShowSuccess(false);
          if (onSuccess) onSuccess();
          handleClose();
        }, 1000);
      }
    } catch (error: any) {
      console.error('Submission error:', error);

      const message =
        error?.response?.data?.message || error?.response?.data?.error || error?.message || 'Something went wrong. Please try again.';

      setApiError(message);
      setShowError(true);
      setTimeout(() => setShowError(false), 5000);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormValues((prev) => ({
      ...prev,
      [field]: value,
    }));

    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: '',
      }));
    }
  };

  const getLocationName = (id: string) => {
    const location = locations.find((loc) => loc.id === Number(id));

    return location ? location.title : '-';
  };

  const getDepartmentName = (id: string) => {
    const department = departments.find((dept) => dept.id === Number(id));

    return department ? department.name : '-';
  };

  const getPlantCodeName = (id: string) => {
    if (!id) return '-';

    const plantCode = plantCodes.find((pc) => pc.id === Number(id));

    return plantCode ? plantCode.title : '-';
  };

  const getPlantIsolationName = (id: string) => {
    if (!id) return '-';

    const isolation = plantIsolations.find((pi) => pi.id === Number(id));

    return isolation ? isolation.title : '-';
  };

  const getResponsiblePersonLabel = (id: string) => {
    if (!id) return '-';

    const person = responsiblePersons.find((p) => p.id === Number(id));

    return person ? person.name : '-';
  };

  const getPlantCodeDisplayText = (plantCode: PlantCode) => {
    return plantCode.title;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex justify-center items-center z-50 p-4">
      <div className="bg-white w-full max-w-[1100px] rounded-lg shadow-xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="relative px-10 pt-8">
          <h2 className="text-xl font-semibold text-gray-900">
            {editId ? 'Edit Plant Safety - Work Permit' : 'Add Plant Safety - Work Permit'}
          </h2>

          <button onClick={handleClose} className="absolute top-8 right-8 text-gray-400 hover:text-gray-600 transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="mt-6 px-12" />

        {/* Loading State */}
        {isLoadingData && (
          <div className="flex items-center justify-center py-8">
            <div className="flex flex-col items-center gap-3">
              <svg className="animate-spin h-8 w-8 text-red-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />

                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>

              <p className="text-gray-600">Loading data...</p>
            </div>
          </div>
        )}

        {!isLoadingData && (
          <>
            {/* Stepper */}
            <div className="relative">
              <div
                className="grid relative"
                style={{
                  gridTemplateColumns: `repeat(${steps.length}, 1fr)`,
                }}
              >
                <div
                  className="absolute bg-gray-300"
                  style={{
                    top: '6px',
                    left: `calc(100% / ${steps.length} / 2)`,
                    right: `calc(100% / ${steps.length} / 2)`,
                    height: '2px',
                  }}
                />

                <div
                  className="absolute bg-green-500 transition-all duration-300"
                  style={{
                    top: '6px',
                    left: `calc(100% / ${steps.length} / 2)`,
                    height: '2px',
                    width: step === 0 ? '0%' : `calc(${step} * (100% - (100% / ${steps.length})) / ${steps.length - 1})`,
                  }}
                />

                {steps.map((label, index) => {
                  const isActive = index === step;

                  const isCompleted = index < step;

                  return (
                    <div key={index} className="flex flex-col items-center">
                      <div className={`w-3 h-3 rounded-full z-10 ${isActive || isCompleted ? 'bg-green-500' : 'bg-gray-300'}`} />

                      <span
                        className={`mt-2 text-sm font-medium whitespace-nowrap ${
                          isActive || isCompleted ? 'text-green-600' : 'text-gray-400'
                        }`}
                      >
                        {label}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto">
              <div className="px-12 py-8">
                {/* STEP 0 */}
                {step === 0 && (
                  <div className="space-y-6">
                    {/* Row 1 */}
                    <div className="grid grid-cols-2 gap-6">
                      {/* Department */}
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          Department
                          <span className="text-red-500">*</span>
                        </label>

                        <div className="relative">
                          <select
                            ref={(el) => (fieldRefs.current.departmentId = el)}
                            value={formValues.departmentId}
                            onChange={(e) => handleChange('departmentId', e.target.value)}
                            className={`w-full px-4 py-2.5 border ${
                              errors.departmentId ? 'border-red-500' : 'border-gray-300'
                            } rounded-md appearance-none bg-gray-100 focus:outline-none ${
                              !formValues.departmentId ? 'text-gray-400' : 'text-gray-900'
                            }`}
                          >
                            <option value="" className="text-gray-500">
                              {isLoadingDepartments ? 'Loading departments...' : 'Select Department'}
                            </option>

                            {departments.map((dept) => (
                              <option key={dept.id} value={dept.id} className="text-gray-900">
                                {dept.name}
                              </option>
                            ))}
                          </select>

                          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                        </div>

                        {errors.departmentId && <p className="text-red-500 text-sm mt-1">{errors.departmentId}</p>}
                      </div>

                      {/* Priority */}
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          Priority
                          <span className="text-red-500">*</span>
                        </label>

                        <div className="relative">
                          <select
                            ref={(el) => (fieldRefs.current.priority = el)}
                            value={formValues.priority}
                            onChange={(e) => handleChange('priority', e.target.value)}
                            className={`w-full px-4 py-2.5 border ${
                              errors.priority ? 'border-red-500' : 'border-gray-300'
                            } rounded-md appearance-none bg-gray-100 focus:outline-none ${
                              !formValues.priority ? 'text-gray-400' : 'text-gray-900'
                            }`}
                          >
                            <option value="">Select Priority</option>

                            <option value="Routine">Routine</option>

                            <option value="Urgent">Urgent</option>

                            <option value="Planned Outage">Planned Outage</option>
                          </select>

                          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                        </div>

                        {errors.priority && <p className="text-red-500 text-sm mt-1">{errors.priority}</p>}
                      </div>
                    </div>

                    {/* EWT */}
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">EWT</label>

                      <input
                        type="text"
                        value={formValues.ewt}
                        onChange={(e) => handleChange('ewt', e.target.value)}
                        placeholder="Enter EWT"
                        className={`w-full px-4 py-2.5 border bg-gray-100 ${
                          errors.ewt ? 'border-red-500' : 'border-gray-300'
                        } rounded-md text-gray-900 placeholder-gray-400 focus:outline-none`}
                      />

                      {errors.ewt && <p className="text-red-500 text-sm mt-1">{errors.ewt}</p>}
                    </div>

                    {/* Location + Unit */}
                    <div className="grid grid-cols-2 gap-6">
                      {/* Location */}
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          Select Location
                          <span className="text-red-500">*</span>
                        </label>

                        <div className="relative">
                          <select
                            ref={(el) => (fieldRefs.current.locationId = el)}
                            value={formValues.locationId}
                            onChange={(e) => handleChange('locationId', e.target.value)}
                            className={`w-full px-4 py-2.5 border ${
                              errors.locationId ? 'border-red-500' : 'border-gray-300'
                            } rounded-md appearance-none bg-gray-100 focus:outline-none ${
                              !formValues.locationId ? 'text-gray-400' : 'text-gray-900'
                            }`}
                          >
                            <option value="">{isLoadingLocations ? 'Loading locations...' : 'Select Location'}</option>

                            {locations.map((location) => (
                              <option key={location.id} value={location.id}>
                                {location.title}
                              </option>
                            ))}
                          </select>

                          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                        </div>

                        {errors.locationId && <p className="text-red-500 text-sm mt-1">{errors.locationId}</p>}
                      </div>

                      {/* Unit */}
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">Unit</label>

                        <input
                          type="text"
                          value={formValues.unit}
                          onChange={(e) => handleChange('unit', e.target.value)}
                          placeholder="Enter Unit"
                          className={`w-full px-4 py-2.5 border bg-gray-100 ${
                            errors.unit ? 'border-red-500' : 'border-gray-300'
                          } rounded-md text-gray-900 placeholder-gray-400 focus:outline-none`}
                        />

                        {errors.unit && <p className="text-red-500 text-sm mt-1">{errors.unit}</p>}
                      </div>
                    </div>

                    {/* Plant Code + Isolation */}
                    <div className="grid grid-cols-2 gap-6">
                      {/* Plant Code */}
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">
                          Plant Code
                          <span className="text-red-500">*</span>
                        </label>

                        <div className="relative">
                          <select
                            ref={(el) => (fieldRefs.current.plantCode = el)}
                            value={formValues.plantCode}
                            onChange={(e) => handleChange('plantCode', e.target.value)}
                            className={`w-full px-4 py-2.5 border ${
                              errors.plantCode ? 'border-red-500' : 'border-gray-300'
                            } rounded-md appearance-none bg-gray-100 focus:outline-none ${
                              !formValues.plantCode ? 'text-gray-400' : 'text-gray-900'
                            }`}
                          >
                            <option value="">{isLoadingPlantCodes ? 'Loading plant codes...' : 'Select Plant Code'}</option>

                            {plantCodes && plantCodes.length > 0
                              ? plantCodes.map((plantCode) => (
                                  <option key={plantCode.id} value={plantCode.id}>
                                    {getPlantCodeDisplayText(plantCode)}
                                  </option>
                                ))
                              : !isLoadingPlantCodes && (
                                  <option value="" disabled>
                                    No plant codes available
                                  </option>
                                )}
                          </select>

                          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                        </div>

                        {errors.plantCode && <p className="text-red-500 text-sm mt-1">{errors.plantCode}</p>}
                      </div>

                      {/* Isolation */}
                      <div>
                        <label className="flex items-center gap-1 text-sm font-medium text-gray-900 mb-2">
                          Plant Requiring Isolation
                          <Info className="w-4 h-4 text-gray-400" />
                          <span className="text-red-500">*</span>
                        </label>

                        <div className="relative">
                          <select
                            ref={(el) => (fieldRefs.current.plantRequiringIsolation = el)}
                            value={formValues.plantRequiringIsolation}
                            onChange={(e) => handleChange('plantRequiringIsolation', e.target.value)}
                            className={`w-full px-4 py-2.5 border ${
                              errors.plantRequiringIsolation ? 'border-red-500' : 'border-gray-300'
                            } rounded-md appearance-none bg-gray-100 focus:outline-none`}
                          >
                            <option value="">
                              {isLoadingPlantIsolations ? 'Loading Plant Requiring Isolation...' : 'Select Plant Requiring Isolation'}
                            </option>

                            {plantIsolations.map((item) => (
                              <option key={item.id} value={item.id}>
                                {item.title}
                              </option>
                            ))}
                          </select>

                          <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" />
                        </div>

                        {errors.plantRequiringIsolation && <p className="text-red-500 text-sm mt-1">{errors.plantRequiringIsolation}</p>}
                      </div>
                    </div>

                    {/* Hazards */}
                    <div>
                      <label className="flex items-center gap-1 text-sm font-medium text-gray-900 mb-2">
                        Describe Hazards
                        <Info className="w-4 h-4 text-gray-400" />
                      </label>

                      <RichTextEditor
                        value={formValues.hazards}
                        onChange={(value) => handleChange('hazards', value)}
                        placeholder="Add description here"
                        error={errors.hazards}
                        maxLength={HAZARDS_MAX_LENGTH}
                      />
                    </div>

                    {/* Work Description */}
                    <div>
                      <label className="flex items-center gap-1 text-sm font-medium text-gray-900 mb-2">
                        Describe Work to be Carried Out
                        <Info className="w-4 h-4 text-gray-400" />
                      </label>

                      <RichTextEditor
                        value={formValues.workDescription}
                        onChange={(value) => handleChange('workDescription', value)}
                        placeholder="Add description here"
                        error={errors.workDescription}
                        maxLength={WORK_DESCRIPTION_MAX_LENGTH}
                      />
                    </div>

                    {/* PPE */}
                    <div>
                      <label className="flex items-center gap-1 text-sm font-medium text-gray-900 mb-2">
                        Enter PPE
                        <Info className="w-4 h-4 text-gray-400" />
                      </label>

                      <input
                        type="text"
                        value={formValues.ppe}
                        onChange={(e) => handleChange('ppe', e.target.value)}
                        placeholder="Enter Apparatus to be Worked On"
                        className={`w-full px-4 py-2.5 border bg-gray-100 ${
                          errors.ppe ? 'border-red-500' : 'border-gray-300'
                        } rounded-md text-gray-900 placeholder-gray-400 focus:outline-none`}
                      />

                      {errors.ppe && <p className="text-red-500 text-sm mt-1">{errors.ppe}</p>}
                    </div>

                    {/* PTW + Environment */}
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">PTW No.</label>

                        <input
                          type="text"
                          value={formValues.ptwNo}
                          readOnly
                          placeholder="Automatically generated on creation"
                          className="w-full px-4 py-2.5 border border-gray-300 bg-gray-200 rounded-md text-gray-900 placeholder-gray-500 cursor-not-allowed focus:outline-none"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">Environment Cert No.</label>

                        <input
                          type="text"
                          value={formValues.environmentCertNo}
                          onChange={(e) => handleChange('environmentCertNo', e.target.value)}
                          placeholder="Enter Environment Cert No."
                          className={`w-full px-4 py-2.5 border bg-gray-100 ${
                            errors.environmentCertNo ? 'border-red-500' : 'border-gray-300'
                          } rounded-md text-gray-900 placeholder-gray-400 focus:outline-none`}
                        />

                        {errors.environmentCertNo && <p className="text-red-500 text-sm mt-1">{errors.environmentCertNo}</p>}
                      </div>
                    </div>

                    {/* Gas Test + Hot Work */}
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">Gas Test Cert No.</label>

                        <input
                          type="text"
                          value={formValues.gasTestCertNo}
                          onChange={(e) => handleChange('gasTestCertNo', e.target.value)}
                          placeholder="Enter Gas Test Cert No."
                          className={`w-full px-4 py-2.5 border bg-gray-100 ${
                            errors.gasTestCertNo ? 'border-red-500' : 'border-gray-300'
                          } rounded-md text-gray-900 placeholder-gray-400 focus:outline-none`}
                        />

                        {errors.gasTestCertNo && <p className="text-red-500 text-sm mt-1">{errors.gasTestCertNo}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">Hot Work Approval Part</label>

                        <input
                          type="text"
                          value={formValues.hotWorkApprovalPart}
                          onChange={(e) => handleChange('hotWorkApprovalPart', e.target.value)}
                          placeholder="Enter Hot Work Approval Part"
                          className={`w-full px-4 py-2.5 border bg-gray-100 ${
                            errors.hotWorkApprovalPart ? 'border-red-500' : 'border-gray-300'
                          } rounded-md text-gray-900 placeholder-gray-400 focus:outline-none`}
                        />

                        {errors.hotWorkApprovalPart && <p className="text-red-500 text-sm mt-1">{errors.hotWorkApprovalPart}</p>}
                      </div>
                    </div>

                    {/* Risk + Safe Entry */}
                    <div className="grid grid-cols-2 gap-6">
                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">Risk Assessment No.</label>

                        <input
                          type="text"
                          value={formValues.riskAssessmentNo}
                          onChange={(e) => handleChange('riskAssessmentNo', e.target.value)}
                          placeholder="Enter Risk Assessment No."
                          className={`w-full px-4 py-2.5 border bg-gray-100 ${
                            errors.riskAssessmentNo ? 'border-red-500' : 'border-gray-300'
                          } rounded-md text-gray-900 placeholder-gray-400 focus:outline-none`}
                        />

                        {errors.riskAssessmentNo && <p className="text-red-500 text-sm mt-1">{errors.riskAssessmentNo}</p>}
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-900 mb-2">Safe Entry Certificate</label>

                        <input
                          type="text"
                          value={formValues.safeEntryCertificate}
                          onChange={(e) => handleChange('safeEntryCertificate', e.target.value)}
                          placeholder="Enter Safe Entry Certificate"
                          className={`w-full px-4 py-2.5 border bg-gray-100 ${
                            errors.safeEntryCertificate ? 'border-red-500' : 'border-gray-300'
                          } rounded-md text-gray-900 placeholder-gray-400 focus:outline-none`}
                        />

                        {errors.safeEntryCertificate && <p className="text-red-500 text-sm mt-1">{errors.safeEntryCertificate}</p>}
                      </div>
                    </div>

                    {/* Work Register */}
                    <div>
                      <label className="block text-sm font-medium text-gray-900 mb-2">Work Register Number</label>

                      <input
                        type="text"
                        value={formValues.workRegisterNumber}
                        onChange={(e) => handleChange('workRegisterNumber', e.target.value)}
                        placeholder="Enter Workers Register No."
                        className={`w-full px-4 py-2.5 border bg-gray-100 ${
                          errors.workRegisterNumber ? 'border-red-500' : 'border-gray-300'
                        } rounded-md text-gray-900 placeholder-gray-400 focus:outline-none`}
                      />

                      {errors.workRegisterNumber && <p className="text-red-500 text-sm mt-1">{errors.workRegisterNumber}</p>}
                    </div>
                  </div>
                )}

                {/* STEP 1 */}
                {step === 1 && (
                  <PlantPermitSignature
                    ref={signatureRef}
                    signatureId={editId ? signatureId : null}
                    initialValues={editId ? signatureValues : undefined}
                  />
                )}

                {/* STEP 2 */}
                {step === 2 && (
                  <div className="space-y-6">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Application</h3>

                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600 mb-1">Department</p>

                          <p className="text-gray-900 font-medium capitalize">{getDepartmentName(formValues.departmentId)}</p>
                        </div>

                        <div>
                          <p className="text-gray-600 mb-1">Priority</p>

                          <p className="text-gray-900 font-medium capitalize">{formValues.priority || '-'}</p>
                        </div>

                        <div>
                          <p className="text-gray-600 mb-1">EWT</p>

                          <p className="text-gray-900 font-medium">{formValues.ewt || '-'}</p>
                        </div>

                        <div>
                          <p className="text-gray-600 mb-1">Location</p>

                          <p className="text-gray-900 font-medium">{getLocationName(formValues.locationId)}</p>
                        </div>

                        <div>
                          <p className="text-gray-600 mb-1">Unit</p>

                          <p className="text-gray-900 font-medium">{formValues.unit || '-'}</p>
                        </div>
                      </div>
                    </div>

                    <div className="border-t pt-4">
                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600 mb-1">Plant Code</p>

                          <p className="text-gray-900 font-medium">{getPlantCodeName(formValues.plantCode)}</p>
                        </div>

                        <div>
                          <p className="text-gray-600 mb-1">Plant Requiring Isolation</p>

                          <p className="text-gray-900 font-medium">{getPlantIsolationName(formValues.plantRequiringIsolation)}</p>
                        </div>
                      </div>
                    </div>

                    {/* Hazards */}
                    <div className="border-t pt-4">
                      <p className="text-gray-600 mb-2 text-sm">Hazards</p>

                      <div className="text-gray-900 text-sm">
                        {formValues.hazards ? (
                          <div
                            className="[&_ul]:list-disc [&_ul]:ml-5 [&_ol]:list-decimal [&_ol]:ml-5 [&_li]:mb-1"
                            dangerouslySetInnerHTML={{
                              __html: formValues.hazards,
                            }}
                          />
                        ) : (
                          '-'
                        )}
                      </div>
                    </div>

                    {/* Work Description */}
                    <div className="border-t pt-4">
                      <p className="text-gray-600 mb-2 text-sm">Work to be Carried Out</p>

                      <div className="text-gray-900 text-sm">
                        {formValues.workDescription ? (
                          <div
                            className="[&_ul]:list-disc [&_ul]:ml-5 [&_ol]:list-decimal [&_ol]:ml-5 [&_li]:mb-1"
                            dangerouslySetInnerHTML={{
                              __html: formValues.workDescription,
                            }}
                          />
                        ) : (
                          '-'
                        )}
                      </div>
                    </div>

                    {/* PPE */}
                    <div className="border-t pt-4">
                      <p className="text-gray-600 mb-2 text-sm">PPE</p>

                      <p className="text-gray-900 font-medium text-sm">{formValues.ppe || '-'}</p>
                    </div>

                    {/* Certificates */}
                    <div className="border-t pt-4">
                      <div className="grid grid-cols-4 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600 mb-1">PTW No.</p>

                          <p className="text-gray-900 font-medium">{formValues.ptwNo || 'Automatically generated on creation'}</p>
                        </div>

                        <div>
                          <p className="text-gray-600 mb-1">Environment Cert No.</p>

                          <p className="text-gray-900 font-medium">{formValues.environmentCertNo || '-'}</p>
                        </div>

                        <div>
                          <p className="text-gray-600 mb-1">Gas Test Cert No.</p>

                          <p className="text-gray-900 font-medium">{formValues.gasTestCertNo || '-'}</p>
                        </div>

                        <div>
                          <p className="text-gray-600 mb-1">Hot Work Approval Part.</p>

                          <p className="text-gray-900 font-medium">{formValues.hotWorkApprovalPart || '-'}</p>
                        </div>

                        <div>
                          <p className="text-gray-600 mb-1">Risk Assessment No.</p>

                          <p className="text-gray-900 font-medium">{formValues.riskAssessmentNo || '-'}</p>
                        </div>

                        <div>
                          <p className="text-gray-600 mb-1">Safe Entry Certificate.</p>

                          <p className="text-gray-900 font-medium">{formValues.safeEntryCertificate || '-'}</p>
                        </div>

                        <div>
                          <p className="text-gray-600 mb-1">Workers Register No.</p>

                          <p className="text-gray-900 font-medium">{formValues.workRegisterNumber || '-'}</p>
                        </div>
                      </div>
                    </div>

                    {/* Signature */}
                    <div className="border-t pt-4">
                      <h3 className="text-lg font-semibold text-gray-900 mb-4">Signature</h3>

                      <div className="grid grid-cols-2 gap-4 text-sm">
                        <div>
                          <p className="text-gray-600 mb-1">Name</p>

                          <p className="text-gray-900 font-medium">{signatureValues.name || '-'}</p>
                        </div>

                        <div>
                          <p className="text-gray-600 mb-1">Responsible Person</p>

                          <p className="text-gray-900 font-medium">{getResponsiblePersonLabel(signatureValues.responsiblePerson)}</p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Footer */}
            <div className="flex justify-end gap-3 px-12 py-6 border-t">
              {step > 0 && (
                <button
                  type="button"
                  onClick={() => setStep(step - 1)}
                  disabled={isSubmitting}
                  className="px-6 py-2.5 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {step === 2 ? 'Previous' : 'Back'}
                </button>
              )}

              <button
                type="button"
                onClick={handleClose}
                disabled={isSubmitting}
                className="px-6 py-2.5 border border-gray-300 rounded-md text-gray-700 font-medium hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>

              <button
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting || (step === 0 && (hazardsOverLimit || workDescriptionOverLimit))}
                className={`px-8 py-2.5 rounded-md font-medium transition-colors flex items-center justify-center gap-2 min-w-[100px] ${
                  isSubmitting || (step === 0 && (hazardsOverLimit || workDescriptionOverLimit))
                    ? 'bg-gray-400 text-white cursor-not-allowed'
                    : 'bg-red-500 text-white hover:bg-red-600'
                }`}
              >
                {isSubmitting && (
                  <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    />
                  </svg>
                )}
                {isSubmitting
                  ? step === steps.length - 1
                    ? editId
                      ? 'Updating...'
                      : 'Adding...'
                    : 'Please wait...'
                  : step === steps.length - 1
                    ? editId
                      ? 'Update'
                      : 'Add'
                    : 'Next'}
              </button>
            </div>
          </>
        )}

        {/* Error Notification */}
        {showError && (
          <div className="fixed bottom-6 right-6 z-[100]">
            <div className="bg-red-500 text-white rounded-lg shadow-lg p-4 pr-12 min-w-[300px] max-w-[500px] relative animate-slide-in">
              <button
                onClick={() => setShowError(false)}
                className="absolute top-3 right-3 text-white hover:text-gray-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="flex items-start gap-3">
                <AlertCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />

                <div>
                  <h4 className="font-semibold mb-1">Error</h4>

                  <p className="text-sm">{apiError}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* SUCCESS NOTIFICATION */}
        {showSuccess && (
          <div className="fixed bottom-6 right-6 z-[100]">
            <div className="bg-green-500 text-white rounded-lg shadow-lg p-4 pr-12 min-w-[300px] max-w-[500px] relative">
              <button
                onClick={() => setShowSuccess(false)}
                className="absolute top-3 right-3 text-white hover:text-gray-200 transition-colors"
              >
                <X className="w-5 h-5" />
              </button>

              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 flex-shrink-0 mt-0.5" />

                <div>
                  <h4 className="font-semibold mb-1">Success</h4>

                  <p className="text-sm">{successMessage}</p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CreatePlantPermitModal;
